import { z } from "zod";

export const entityTypes = [
  "tasks", "task-templates", "timeline", "budgets", "vendors", "core-vendors", "decorations",
  "stationery-gifts", "guests", "tables", "schedules", "shots", "items",
  "assignments", "risks", "ideas",
] as const;
export type EntityType = (typeof entityTypes)[number];

export const budgetCategories = ["婚宴","婚庆","四大金刚","婚纱礼服","珠宝戒指","花艺","喜糖","请柬","伴手礼","婚车","酒水","住宿","交通","摄影摄像额外费用","父母服装","伴郎伴娘礼物","应急备用金"] as const;
export const vendorTypes = ["婚礼策划","婚庆公司","酒店 / 婚宴场地","化妆师","司仪","摄影师","摄像师","婚车","婚纱礼服","西装","跟妆","试妆","现场督导","DJ","音响","灯光","花艺师","喜糖供应商","请柬设计或印刷","伴手礼供应商","婚礼蛋糕","酒水供应商","婚礼网站或小程序","住宿酒店","交通接送","After Party 场地","婚礼香氛","定制贴纸","火漆","礼袋丝带","待选择"] as const;

export const roleSchema = z.enum(["owner", "admin", "editor", "viewer", "vendor", "guest"]);
export type Role = z.infer<typeof roleSchema>;

export const baseEntitySchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  schemaVersion: z.number().int().positive().default(1),
  version: z.number().int().positive().default(1),
  createdAt: z.string().datetime(),
  createdBy: z.string(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string(),
  deletedAt: z.string().datetime().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
});

export const recordSchema = baseEntitySchema.extend({
  entityType: z.enum(entityTypes),
  data: z.record(z.string(), z.unknown()),
});
export type EntityRecord = z.infer<typeof recordSchema>;

export const workspaceSettingsSchema = z.object({
  projectName: z.string().min(1), groomName: z.string().default("新郎"), brideName: z.string().default("新娘"),
  weddingDate: z.string().date(), preparationStartDate: z.string().date(), timezone: z.string().default("Asia/Shanghai"),
  currency: z.string().default("CNY"), region: z.string().default("中国"), ceremonyStyle: z.string().default("酒店婚礼"),
  city: z.string().default("待确认"), guestTarget: z.number().int().nonnegative().default(200), tableCount: z.number().int().nonnegative().default(20),
  tableCapacity: z.number().int().positive().default(10), budgetLimitCents: z.number().int().nonnegative().default(0), emergencyFundRate: z.number().min(0).max(1).default(0.1),
  primaryColor: z.string().default("#B69A64"), secondaryColors: z.array(z.string()).default(["#F7F0E3", "#8A9A7B"]),
  logoUrl: z.string().url().optional(), monogram: z.string().default("囍"), status: z.enum(["未开始", "筹备中", "已完成", "已归档"]).default("未开始"),
  bufferDays: z.number().int().nonnegative().default(10), rsvpDeadline: z.string().date().optional(),
});
export type WorkspaceSettings = z.infer<typeof workspaceSettingsSchema>;

export const workspaceSchema = baseEntitySchema.extend({
  entityType: z.literal("workspace"), settings: workspaceSettingsSchema,
});
export type Workspace = z.infer<typeof workspaceSchema>;

export const mutationSchema = z.object({
  data: z.record(z.string(), z.unknown()), expectedVersion: z.number().int().positive().optional(),
});

export const apiSuccessSchema = <T extends z.ZodType>(schema: T) => z.object({
  success: z.literal(true), data: schema, meta: z.object({ requestId: z.string(), cursor: z.string().optional(), generatedAt: z.string() }),
});

export type ApiSuccess<T> = { success: true; data: T; meta: { requestId: string; cursor?: string; generatedAt: string } };
export type DeletionReference = {
  entityType: EntityType;
  entityId: string;
  moduleLabel: string;
  recordLabel: string;
  relationLabel: string;
};
export type ApiFailure = { success: false; error: { code: string; message: string; fieldErrors?: unknown }; meta: { requestId: string } };
